"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence, useInView } from "motion/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Calendar,
  Users,
  Award,
  Building,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { container } from "@/lib/framer";
// import { cardVariants, container } from "@/lib/framer";

// Define gallery item type
type GalleryItem = {
  id: number;
  src: string;
  alt: string;
  category: string;
  year: number;
  description: string;
  featured?: boolean;
};

// Define timeline event type
type TimelineEvent = {
  year: number;
  title: string;
  description: string;
  image: string;
};

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  // const [searchQuery, setSearchQuery] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const timelineRef = useRef(null);
  const isTimelineInView = useInView(timelineRef, { once: true, amount: 0.2 });

  const [activeCategory, setActiveCategory] = useState("all");
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // For infinite scroll observer
  // const observer = useRef<IntersectionObserver | null>(null);

  // Fetch initial images (reset list) when category changes
  const fetchInitialImages = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL("/api/images", window.location.origin);
      url.searchParams.append("category", activeCategory);
      url.searchParams.append("limit", "5");
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Failed to fetch images");
      const data = await res.json();
      setItems(data.images);
      setNextPageToken(data.nextPageToken || null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  // Fetch more images for infinite scrolling (append to list)
  const fetchMoreImages = useCallback(async () => {
    // If there's no nextPageToken, nothing to load
    if (!nextPageToken) return;
    setLoading(true);
    try {
      const url = new URL("/api/images", window.location.origin);
      url.searchParams.append("category", activeCategory);
      url.searchParams.append("limit", "5");
      url.searchParams.append("pageToken", nextPageToken);
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Failed to fetch images");
      const data = await res.json();
      setItems((prev) => [...prev, ...data.images]);
      setNextPageToken(data.nextPageToken || null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, nextPageToken]);

  // Reset images when the active category changes
  useEffect(() => {
    setItems([]);
    setNextPageToken(null);
    fetchInitialImages();
  }, [activeCategory, fetchInitialImages]);

  // Infinite scrolling: observe the last image element
  // const lastItemRef = useCallback(
  //   (node: HTMLDivElement) => {
  //     if (loading) return;
  //     if (observer.current) observer.current.disconnect();
  //     observer.current = new IntersectionObserver((entries) => {
  //       if (entries[0].isIntersecting && nextPageToken) {
  //         fetchMoreImages();
  //       }
  //     });
  //     if (node) observer.current.observe(node);
  //   },
  //   [loading, nextPageToken, fetchMoreImages]
  // );

  // Gallery items data
  // const galleryItems: GalleryItem[] = [
  //   {
  //     id: 1,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "School founding ceremony in 1995",
  //     category: "historical",
  //     year: 1995,
  //     description:
  //       "The inaugural ceremony when Shree Janakalyan Boarding School was established with just 2 classrooms and 25 students.",
  //     featured: true,
  //   },
  //   {
  //     id: 2,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "First batch of students in 1995",
  //     category: "students",
  //     year: 1995,
  //     description:
  //       "The first batch of students who enrolled in our school when we opened our doors.",
  //   },
  //   {
  //     id: 3,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Original school building in 1995",
  //     category: "campus",
  //     year: 1995,
  //     description:
  //       "The original school building with just two classrooms when we first started.",
  //   },
  //   {
  //     id: 4,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "First expansion in 2000",
  //     category: "campus",
  //     year: 2000,
  //     description:
  //       "The school's first major expansion, adding a new building with 6 more classrooms and a small library.",
  //     featured: true,
  //   },
  //   {
  //     id: 5,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "First annual sports day in 2001",
  //     category: "events",
  //     year: 2001,
  //     description:
  //       "Our first annual sports day celebration with students participating in various athletic competitions.",
  //   },
  //   {
  //     id: 6,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Computer lab inauguration in 2005",
  //     category: "facilities",
  //     year: 2005,
  //     description:
  //       "Inauguration of our first computer lab with 10 computers, marking a significant step in technological integration.",
  //   },
  //   {
  //     id: 7,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Cultural program in 2007",
  //     category: "events",
  //     year: 2007,
  //     description:
  //       "Students performing traditional Nepali dances during our annual cultural program.",
  //   },
  //   {
  //     id: 8,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Science exhibition in 2010",
  //     category: "academic",
  //     year: 2010,
  //     description:
  //       "Students showcasing their science projects during our first science exhibition.",
  //   },
  //   {
  //     id: 9,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "New science lab in 2012",
  //     category: "facilities",
  //     year: 2012,
  //     description:
  //       "Our newly constructed science laboratory with modern equipment for hands-on learning.",
  //   },
  //   {
  //     id: 10,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "School's 15th anniversary celebration in 2010",
  //     category: "events",
  //     year: 2010,
  //     description:
  //       "The entire school community gathered to celebrate our 15th anniversary milestone.",
  //     featured: true,
  //   },
  //   {
  //     id: 11,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Campus modernization in 2015",
  //     category: "campus",
  //     year: 2015,
  //     description:
  //       "The renovated campus with improved facilities including a science lab and art studio.",
  //     featured: true,
  //   },
  //   {
  //     id: 12,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "National Award for Educational Excellence in 2020",
  //     category: "achievements",
  //     year: 2020,
  //     description:
  //       "Our principal receiving the National Award for Educational Excellence in Primary Education.",
  //     featured: true,
  //   },
  //   {
  //     id: 13,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Smart classroom implementation in 2023",
  //     category: "facilities",
  //     year: 2023,
  //     description:
  //       "One of our newly implemented smart classrooms with interactive digital learning tools.",
  //     featured: true,
  //   },
  //   {
  //     id: 14,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Students in classroom 2023",
  //     category: "students",
  //     year: 2023,
  //     description:
  //       "Students engaged in collaborative learning activities in our modern classrooms.",
  //   },
  //   {
  //     id: 15,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Annual sports day 2022",
  //     category: "events",
  //     year: 2022,
  //     description:
  //       "Students participating in our annual sports day competitions.",
  //   },
  //   {
  //     id: 16,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Art exhibition 2021",
  //     category: "events",
  //     year: 2021,
  //     description:
  //       "Student artwork displayed during our annual art exhibition.",
  //   },
  //   {
  //     id: 17,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Teacher training workshop 2019",
  //     category: "staff",
  //     year: 2019,
  //     description:
  //       "Our teachers participating in a professional development workshop.",
  //   },
  //   {
  //     id: 18,
  //     src: "/placeholder.svg?height=400&width=600",
  //     alt: "Library expansion 2018",
  //     category: "facilities",
  //     year: 2018,
  //     description:
  //       "Our expanded library with a wider collection of books and digital resources.",
  //   },
  // ];

  // Timeline events data (converted to Gregorian calendar)
  const timelineEvents: TimelineEvent[] = [
    {
      year: 2052,
      title: "Foundation",
      description:
        "Shree Janakalyan Boarding School was established with just 2 classrooms and 24 students.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061265/enhancefirst_ahp2w7.jpg",
    },
    {
      year: 2057,
      title: "First Expansion",
      description:
        "Added a new building with 6 more classrooms and a small library.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061081/constru1_q7g3e7.jpg",
    },
    {
      year: 2064,
      title: "Curriculum Enhancement",
      description:
        "Introduced comprehensive English medium curriculum and cultural programs.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061090/curienhan_cibr6c.jpg",
    },
    {
      year: 2069,
      title: "Technology Integration",
      description:
        "Established our first computer lab with 15 computers for students.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061088/techinte_mlsopa.jpg",
    },
    {
      year: 2072,
      title: "International Collaboration",
      description:
        "Collaborated with international universities for exchange programs.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742046123/college_txe5e6.jpg",
    },
    {
      year: 2076,
      title: "Campus Modernization",
      description:
        "Renovated all facilities and added a science lab and playground.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061085/buss_riha3y.jpg",
    },
    {
      year: 2081,
      title: "Excellence Recognition",
      description:
        "Received Award for Educational Excellence in Primary Education.",
      image:
        "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742061566/prize_vkctpx.jpg",
    },
  ];

  // Filter gallery items based on active category and search query
  // const filteredItems = galleryItems.filter((item) => {
  //   const matchesCategory =
  //     activeCategory === "all" || item.category === activeCategory;
  //   const matchesSearch =
  //     item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     item.alt.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     item.year.toString().includes(searchQuery);
  //   return matchesCategory && matchesSearch;
  // });

  // Get featured items
  // const featuredItems = galleryItems.filter((item) => item.featured);

  // Handle lightbox navigation
  const navigateLightbox = (direction: "next" | "prev") => {
    if (!selectedImage) return;

    const currentIndex = items.findIndex(
      (item) => item.id === selectedImage.id
    );
    let newIndex = currentIndex;

    if (direction === "next") {
      newIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    }

    setSelectedImage(items[newIndex]);
    setLightboxIndex(newIndex);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <section className="relative bg-muted py-8 md:py-10">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <Link
              href="/"
              className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl font-bold tracking-tight mb-4">
                School History Gallery
              </h1>
              <p className="text-muted-foreground mb-6">
                Explore the remarkable journey of Shree Janakalyan Boarding
                School from its humble beginnings in 1995 to the present day.
                This visual timeline showcases nearly three decades of growth,
                achievements, and memorable moments that have shaped our
                educational legacy.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild>
                  <a href="#timeline">View Timeline</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="#gallery">Browse Gallery</a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              className="relative h-[300px] md:h-[400px] overflow-hidden rounded-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Image
                src="https://res.cloudinary.com/ds1npuv1v/image/upload/v1742047808/Web_Photo_Editor_j1ofxe.jpg"
                alt="School history collage"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                <p className="text-white text-lg font-medium">
                  Nearly 30 Years of Educational Excellence
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section id="timeline" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Our Journey Through Time
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore the key milestones in the history of Shree Janakalyan
              Boarding School from our founding to the present day.
            </p>
          </motion.div>

          <div className="relative mt-16 pb-16 px-4 md:px-8" ref={timelineRef}>
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-primary/40 via-primary/60 to-primary/40 rounded-full z-10">
              <div className="absolute inset-0 w-1 bg-primary/20 rounded-full blur-sm -z-10"></div>
            </div>

            {/* Timeline events */}
            {timelineEvents.map((event, index) => (
              <motion.div
                key={event.year}
                className={`mb-20 md:mb-28 relative group ${index % 2 === 0 ? "md:text-right" : ""}`}
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isTimelineInView
                    ? { opacity: 1, y: 0 }
                    : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <div className="md:grid md:grid-cols-2 md:gap-12 lg:gap-16 items-center">
                  <div
                    className={`${index % 2 === 0 ? "md:order-1" : "md:order-2"}`}
                  >
                    {/* Year marker */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 top-8 md:top-1/2 z-20">
                      <motion.div
                        className="relative group"
                        whileHover={{ scale: 1.1 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        {/* Outer ring for emphasis */}
                        {/* <div className="w-16 h-16 rounded-full bg-primary/20 absolute inset-0 -z-10 group-hover:bg-primary/30 transition-colors duration-300"></div> */}

                        {/* Main year marker */}
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-gray-50 dark:text-gray-900 text-lg font-bold shadow-2xl border-4 border-background relative z-10">
                          {event.year}
                        </div>

                        {/* Glow effect */}
                        <div className="absolute inset-0 w-14 h-14 rounded-full bg-primary opacity-10 blur-sm -z-20"></div>
                      </motion.div>
                    </div>

                    <div className="md:pt-0 pt-24 md:px-4">
                      <div className="mb-4">
                        <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-2">
                          {event.year}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold mb-4 text-foreground group-hover:text-primary transition-colors duration-300">
                        {event.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {event.description}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`${index % 2 === 0 ? "md:order-2" : "md:order-1"} mt-6 md:mt-0 relative z-0 md:px-4`}
                  >
                    <motion.div
                      className="relative h-48 md:h-64 lg:h-72 overflow-hidden rounded-lg shadow-lg mx-2 md:mx-0"
                      whileHover={{ scale: 1.03 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                    >
                      <Image
                        src={event.image || "/placeholder.svg"}
                        alt={`${event.title} - ${event.year}`}
                        fill
                        className="object-cover"
                      />
                      {/* Subtle overlay to ensure timeline markers stand out */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent md:bg-none"></div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Photos Section */}
      {/* <section className="py-16 md:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Featured Moments
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Significant milestones and memorable events that have shaped our
              school&apos;s history.
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featuredItems.map((item, index) => (
              <motion.div
                key={item.id}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => setSelectedImage(item)}
                className="cursor-pointer"
              >
                <Card className="overflow-hidden h-full">
                  <div className="relative h-48 md:h-64">
                    <Image
                      src={item.src || "/placeholder.svg"}
                      alt={item.alt}
                      fill
                      className="object-cover transition-transform duration-300 hover:scale-105"
                    />
                    <div className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                      {item.year}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold mb-2">{item.alt}</h3>
                    <p className="text-sm text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Main Gallery Section */}
      <section id="gallery" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Photo Gallery
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Browse through our collection of photographs documenting the
              school&apos;s history, events, and growth over the years.
            </p>
          </motion.div>

          {/* Search and Filter */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center ">
              {/* <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search gallery..."
                  className="w-full pl-10 pr-4 py-2 rounded-md border border-input bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div> */}
              <Tabs
                defaultValue="all"
                className="w-full md:w-auto my-4"
                onValueChange={(value) => setActiveCategory(value)}
              >
                <TabsList className="grid grid-cols-2 md:grid-cols-7 w-full md:w-auto">
                  <TabsTrigger value="all" className="text-xs md:text-sm">
                    All
                  </TabsTrigger>
                  <TabsTrigger
                    value="historical"
                    className="text-xs md:text-sm"
                  >
                    Historical
                  </TabsTrigger>
                  <TabsTrigger value="campus" className="text-xs md:text-sm">
                    Campus
                  </TabsTrigger>
                  <TabsTrigger value="students" className="text-xs md:text-sm">
                    Students
                  </TabsTrigger>
                  <TabsTrigger value="events" className="text-xs md:text-sm">
                    Events
                  </TabsTrigger>
                  <TabsTrigger
                    value="facilities"
                    className="text-xs md:text-sm"
                  >
                    Facilities
                  </TabsTrigger>
                  <TabsTrigger
                    value="achievements"
                    className="text-xs md:text-sm"
                  >
                    Achievements
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Gallery Grid */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {items.length > 0 ? (
              items.map((item) => (
                <motion.div
                  key={item.id}
                  onClick={() => setSelectedImage(item)}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="cursor-pointer"
                  // ref={index === items.length - 1 ? lastItemRef : null}
                >
                  <div className="relative aspect-square overflow-hidden rounded-lg group">
                    <Image
                      src={item.src || "/placeholder.svg"}
                      alt={item.alt}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full py-12 text-center">
                <p className="text-muted-foreground">
                  No images found for the selected category.
                </p>
              </div>
            )}

            {/* {filteredItems.length > 0 ? (
              filteredItems.map((galleryItem) => (
                <motion.div
                  key={galleryItem.id}
                  variants={item}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className="cursor-pointer"
                  onClick={() => setSelectedImage(galleryItem)}
                >
                  <div className="relative aspect-square overflow-hidden rounded-lg group">
                    <Image
                      src={galleryItem.src || "/placeholder.svg"}
                      alt={galleryItem.alt}
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                      <span className="text-white font-medium">
                        {galleryItem.alt}
                      </span>
                      <span className="text-white/80 text-sm">
                        {galleryItem.year}
                      </span>
                    </div>
                    <div className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                      {galleryItem.year}
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full py-12 text-center">
                <p className="text-muted-foreground">
                  No images found matching your search criteria.
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("all");
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )} */}
          </motion.div>
          {nextPageToken && !loading && (
            <div className="flex justify-center my-4">
              <button
                onClick={fetchMoreImages}
                className="px-4 py-2 bg-primary text-white rounded-md"
              >
                Load More
              </button>
            </div>
          )}
          {loading && (
            <p className="text-center my-4">Loading more images...</p>
          )}
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Calendar className="h-10 w-10 mx-auto mb-4 text-primary-foreground/80" />
              <div className="text-4xl font-bold mb-2">29+</div>
              <div className="text-sm text-primary-foreground/80">
                Years of Excellence
              </div>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Users className="h-10 w-10 mx-auto mb-4 text-primary-foreground/80" />
              <div className="text-4xl font-bold mb-2">200+</div>
              <div className="text-sm text-primary-foreground/80">
                Current Students
              </div>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Building className="h-10 w-10 mx-auto mb-4 text-primary-foreground/80" />
              <div className="text-4xl font-bold mb-2">2</div>
              <div className="text-sm text-primary-foreground/80">
                Campus Buildings
              </div>
            </motion.div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Award className="h-10 w-10 mx-auto mb-4 text-primary-foreground/80" />
              <div className="text-4xl font-bold mb-2">48+</div>
              <div className="text-sm text-primary-foreground/80">
                Awards Received
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
              Be Part of Our Future
            </h2>
            <p className="text-muted-foreground mb-8">
              Join us as we continue to build on our rich history and create new
              milestones in educational excellence. Apply for admission or
              schedule a visit to our campus today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link href="/online-admission">Apply for Admission</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/#contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative max-w-5xl w-full max-h-[90vh] flex flex-col"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 15 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
              >
                <X className="h-6 w-6" />
              </button>

              <div className="relative w-full h-[70vh]">
                <Image
                  src={selectedImage.src || "/placeholder.svg"}
                  alt={selectedImage.alt}
                  fill
                  className="object-contain"
                />
              </div>

              <div className="bg-black/70 p-4 md:p-6 text-white mt-auto">
                <h3 className="text-lg md:text-xl font-bold mb-2">
                  {selectedImage.alt}
                </h3>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm md:text-base font-medium bg-primary/20 px-2 py-1 rounded">
                    {selectedImage.year}
                  </span>
                </div>
                <p className="text-white/80 text-sm md:text-base leading-relaxed">
                  {selectedImage.description}
                </p>
              </div>

              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <button
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateLightbox("prev");
                  }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              </div>

              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <button
                  className="bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateLightbox("next");
                  }}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>

              <div className="absolute bottom-20 left-0 right-0 flex justify-center gap-2">
                <div className="bg-black/70 px-3 py-1 rounded-full text-white/90 text-sm">
                  {lightboxIndex + 1} / {items.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
